import { RTC_PROVIDERS } from '../providerIds'
import { normalizeResolvedVideoCallQuality } from '../routing/videoCallQualityContract'

export function buildLocalPeerMetadata(localParticipantIdentity) {
  if (!localParticipantIdentity?.participantId) {
    return null
  }

  return {
    participantId: localParticipantIdentity.participantId,
    identityKey: localParticipantIdentity.participantId,
    userId: localParticipantIdentity.userId || null,
    actorType: localParticipantIdentity.actorType || null,
    actorId: localParticipantIdentity.actorId || null,
    displayName: localParticipantIdentity.displayName || null,
    profilePicId: localParticipantIdentity.profilePicId || null,
    role: localParticipantIdentity.role || null,
  }
}

export function buildHundredMsRtcProps(
  sessionRoutingDecision,
  agoraRtcProps,
  localParticipantIdentity,
) {
  const hundredMsPayload = sessionRoutingDecision?.providerPayloads?.[RTC_PROVIDERS.hundredMs]
  const resolvedVideoQuality = normalizeResolvedVideoCallQuality(
    hundredMsPayload?.videoQuality ||
      sessionRoutingDecision?.videoQuality ||
      agoraRtcProps?.videoQuality,
  )
  if (!hundredMsPayload?.authToken || !hundredMsPayload?.userName) {
    return null
  }

  return {
    authToken: hundredMsPayload.authToken,
    userName: hundredMsPayload.userName,
    role: hundredMsPayload.role,
    roomId: hundredMsPayload.roomId,
    roomName: hundredMsPayload.roomName,
    userId: hundredMsPayload.userId,
    peerMetadata:
      hundredMsPayload.peerMetadata || buildLocalPeerMetadata(localParticipantIdentity),
    rememberDeviceSelection: hundredMsPayload.rememberDeviceSelection,
    autoManageVideo: hundredMsPayload.autoManageVideo,
    autoManageWakeLock: hundredMsPayload.autoManageWakeLock,
    enableAudio: agoraRtcProps?.enableAudio,
    enableVideo: agoraRtcProps?.enableVideo,
    enableScreensharing:
      hundredMsPayload.enableScreensharing ??
      sessionRoutingDecision?.capabilities?.screenshare === true,
    cameraDeviceId: agoraRtcProps?.cameraDeviceId,
    microphoneDeviceId: agoraRtcProps?.microphoneDeviceId,
    playbackDeviceId: agoraRtcProps?.playbackDeviceId,
    layout: agoraRtcProps?.layout,
    videoQuality: resolvedVideoQuality,
  }
}

export function buildDailyRtcProps(
  sessionRoutingDecision,
  agoraRtcProps,
  localParticipantIdentity,
) {
  const dailyPayload = sessionRoutingDecision?.providerPayloads?.[RTC_PROVIDERS.dailyCo]
  const resolvedVideoQuality = normalizeResolvedVideoCallQuality(
    dailyPayload?.videoQuality ||
      sessionRoutingDecision?.videoQuality ||
      agoraRtcProps?.videoQuality,
  )
  if (!dailyPayload?.roomUrl) {
    return null
  }

  return {
    roomUrl: dailyPayload.roomUrl,
    token: dailyPayload.token,
    userName:
      dailyPayload.userName ||
      localParticipantIdentity?.displayName ||
      'Daily participant',
    userId: dailyPayload.userId || localParticipantIdentity?.userId || null,
    participantMetadata:
      dailyPayload.participantMetadata || buildLocalPeerMetadata(localParticipantIdentity),
    fixtureParticipants: Array.isArray(dailyPayload.fixtureParticipants)
      ? dailyPayload.fixtureParticipants
      : undefined,
    initialSessionState: dailyPayload.initialSessionState,
    enableAudio: agoraRtcProps?.enableAudio,
    enableVideo: agoraRtcProps?.enableVideo,
    enableScreensharing:
      dailyPayload.enableScreensharing ??
      sessionRoutingDecision?.capabilities?.screenshare === true,
    cameraDeviceId: agoraRtcProps?.cameraDeviceId,
    microphoneDeviceId: agoraRtcProps?.microphoneDeviceId,
    playbackDeviceId: agoraRtcProps?.playbackDeviceId,
    layout: agoraRtcProps?.layout,
    videoQuality: resolvedVideoQuality,
  }
}

export function getParticipantTransportKey(participant, providerId) {
  if (!participant) {
    return null
  }

  if (providerId === RTC_PROVIDERS.hundredMs) {
    return participant?.transport?.peerId || null
  }

  if (providerId === RTC_PROVIDERS.dailyCo) {
    return (
      participant?.transport?.sessionId ??
      participant?.transport?.dailySessionId ??
      null
    )
  }

  return participant?.transport?.uid ?? participant?.transport?.rtcUid ?? null
}
